import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useTheme } from "../../../contexts/ThemeContext";
import { useAuth } from "../../../contexts/AuthContext";
import LayoutDesktop from "../../../layouts/LayoutDesktop";

import { db } from "../../../config/firebase";
import {
  collection,
  doc,
  getDocs,
  query,
  where,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { obterProximoCodigoSequencialPeca } from "./obterProximoCodigoSequencialPeca"; // ou deixe no mesmo arquivo

function NovaPeca() {
  const { tema } = useTheme();
  const { usuario: usuarioLogado } = useAuth();
  const navigate = useNavigate();

  const [fornecedores, setFornecedores] = useState([]);
  const [maquinas, setMaquinas] = useState([]);

  const [carregandoFornecedores, setCarregandoFornecedores] = useState(false);
  const [carregandoMaquinas, setCarregandoMaquinas] = useState(false);

  const [nome, setNome] = useState("");
  const [fornecedorId, setFornecedorId] = useState("");
  const [maquinaId, setMaquinaId] = useState("");
  const [descricao, setDescricao] = useState("");
  const [ondeSeAplica, setOndeSeAplica] = useState("");
  const [fotosTexto, setFotosTexto] = useState("");
  const [observacoes, setObservacoes] = useState("");

  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");
  const [carregando, setCarregando] = useState(false);

  useEffect(() => {
    async function carregarFornecedoresEMaquinas() {
      if (!usuarioLogado?.grupoId) return;

      const grupoId = usuarioLogado.grupoId;
      const empresaId = usuarioLogado.empresaId || null;

      // Fornecedores
      try {
        setCarregandoFornecedores(true);
        const fornecedoresRef = collection(db, "fornecedores");

        let qF;
        if (empresaId) {
          qF = query(
            fornecedoresRef,
            where("grupoId", "==", grupoId),
            where("empresaId", "==", empresaId)
          );
        } else {
          qF = query(fornecedoresRef, where("grupoId", "==", grupoId));
        }

        const snapF = await getDocs(qF);
        const listaF = snapF.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setFornecedores(listaF);
      } catch (e) {
        console.error("Erro ao carregar fornecedores:", e);
        setErro((prev) =>
          prev ? prev + " / Erro ao carregar fornecedores." : "Erro ao carregar fornecedores."
        );
      } finally {
        setCarregandoFornecedores(false);
      }

      // Máquinas
      try {
        setCarregandoMaquinas(true);
        const maquinasRef = collection(db, "maquinas");

        let qM;
        if (empresaId) {
          qM = query(
            maquinasRef,
            where("grupoId", "==", grupoId),
            where("empresaId", "==", empresaId)
          );
        } else {
          qM = query(maquinasRef, where("grupoId", "==", grupoId));
        }

        const snapM = await getDocs(qM);
        const listaM = snapM.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setMaquinas(listaM);
      } catch (e) {
        console.error("Erro ao carregar máquinas:", e);
        setErro((prev) =>
          prev ? prev + " / Erro ao carregar máquinas." : "Erro ao carregar máquinas."
        );
      } finally {
        setCarregandoMaquinas(false);
      }
    }

    carregarFornecedoresEMaquinas();
  }, [usuarioLogado]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro("");
    setSucesso("");

    if (!usuarioLogado?.grupoId) {
      setErro(
        "Usuário logado não possui grupo definido. Verifique o cadastro do Gestor."
      );
      return;
    }

    if (!nome.trim()) {
      setErro("Informe o nome da peça.");
      return;
    }

    const grupoId = usuarioLogado.grupoId;
    const grupoNome = usuarioLogado.grupoNome || usuarioLogado.grupo || null;
    const empresaId = usuarioLogado.empresaId || null;
    const empresaNome = usuarioLogado.empresaNome || null;

    const fornecedorSelecionado = fornecedores.find((f) => f.id === fornecedorId);
    const fornecedorNome = fornecedorSelecionado?.nome || null;

    const maquinaSelecionada = maquinas.find((m) => m.id === maquinaId);
    const maquinaNome = maquinaSelecionada?.nome || null;

    const fotosArray = fotosTexto
      .split("\n")
      .map((l) => l.trim())
      .filter((l) => l.length > 0);

    setCarregando(true);

    try {
      const sequencial = await obterProximoCodigoSequencialPeca(grupoId);
      const codigo = `PE${String(sequencial).padStart(4, "0")}`;

      const novaRef = doc(collection(db, "pecas"));
      await setDoc(novaRef, {
        codigo,
        codigoSequencial: sequencial,

        nome: nome.trim(),
        fornecedorId: fornecedorId || null,
        fornecedorNome,
        maquinaId: maquinaId || null,
        maquinaNome,
        descricao: descricao.trim() || null,
        ondeSeAplica: ondeSeAplica.trim() || null,
        fotos: fotosArray.length ? fotosArray : [],
        observacoes: observacoes.trim() || null,

        grupoId,
        grupoNome: grupoNome || null,
        empresaId,
        empresaNome,

        criadoPor: usuarioLogado?.uid || null,
        criadoEm: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setSucesso(`Peça criada com sucesso (código ${codigo}).`);

      setNome("");
      setFornecedorId("");
      setMaquinaId("");
      setDescricao("");
      setOndeSeAplica("");
      setFotosTexto("");
      setObservacoes("");
    } catch (err) {
      console.error(err);
      setErro("Erro ao salvar peça. Tente novamente.");
    } finally {
      setCarregando(false);
    }
  };

  return (
    <LayoutDesktop
      tema={tema}
      titulo="Nova Peça"
      subtitulo="Cadastros / Peças / Novo"
    >
      <section style={{ width: "100%" }}>
        <div
          className="pc-card"
          style={{
            width: "100%",
            paddingLeft: 40,
            paddingRight: 40,
            boxSizing: "border-box",
          }}
        >
          <header className="pc-table-header">
            <div className="pc-table-header-text">
              <h3>Cadastro de peça</h3>
              <p>
                Cadastre peças vinculadas a fornecedores e máquinas do grupo atual.
              </p>
            </div>
          </header>

          {erro && (
            <div
              style={{
                marginTop: 12,
                padding: 8,
                borderRadius: 8,
                background: "#7f1d1d",
                color: "#fecaca",
                fontSize: 14,
              }}
            >
              {erro}
            </div>
          )}

          {sucesso && (
            <div
              style={{
                marginTop: 12,
                padding: 8,
                borderRadius: 8,
                background: "#14532d",
                color: "#bbf7d0",
                fontSize: 14,
              }}
            >
              {sucesso}
            </div>
          )}

          <form
            onSubmit={handleSubmit}
            style={{
              marginTop: 12,
              maxWidth: 720,
              marginLeft: "auto",
              marginRight: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            {/* Nome */}
            <label>
              <span style={{ fontSize: 14 }}>Nome</span>
              <input
                type="text"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                required
                style={{
                  width: "100%",
                  padding: "9px 11px",
                  marginTop: 4,
                  borderRadius: 8,
                  border: "1px solid #334155",
                  
                }}
              />
            </label>

            {/* Fornecedor */}
            <label>
              <span style={{ fontSize: 14 }}>Fornecedor</span>
              <select
                value={fornecedorId}
                onChange={(e) => setFornecedorId(e.target.value)}
                disabled={carregandoFornecedores}
                style={{
                  width: "100%",
                  padding: "9px 11px",
                  marginTop: 4,
                  borderRadius: 8,
                  border: "1px solid #334155",
                  
                  color: "inherit",
                }}
              >
                <option value="">
                  {carregandoFornecedores
                    ? "Carregando fornecedores..."
                    : "Selecione o fornecedor"}
                </option>
                {fornecedores.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.nome || "(sem nome)"}
                  </option>
                ))}
              </select>
            </label>

            {/* Máquina */}
            <label>
              <span style={{ fontSize: 14 }}>Máquina</span>
              <select
                value={maquinaId}
                onChange={(e) => setMaquinaId(e.target.value)}
                disabled={carregandoMaquinas}
                style={{
                  width: "100%",
                  padding: "9px 11px",
                  marginTop: 4,
                  borderRadius: 8,
                  border: "1px solid #334155",
                  
                  color: "inherit",
                }}
              >
                <option value="">
                  {carregandoMaquinas
                    ? "Carregando máquinas..."
                    : "Selecione a máquina (opcional)"}
                </option>
                {maquinas.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.nome || "(sem nome)"}
                  </option>
                ))}
              </select>
            </label>

            {/* Descrição */}
            <label>
              <span style={{ fontSize: 14 }}>Descrição</span>
              <textarea
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                rows={3}
                style={{
                  width: "100%",
                  padding: "9px 11px",
                  marginTop: 4,
                  borderRadius: 8,
                  border: "1px solid #334155",
                  
                  resize: "vertical",
                }}
              />
            </label>

            {/* Onde se Aplica */}
            <label>
              <span style={{ fontSize: 14 }}>Onde se aplica</span>
              <textarea
                value={ondeSeAplica}
                onChange={(e) => setOndeSeAplica(e.target.value)}
                rows={2}
                style={{
                  width: "100%",
                  padding: "9px 11px",
                  marginTop: 4,
                  borderRadius: 8,
                  border: "1px solid #334155",
                  
                  resize: "vertical",
                }}
              />
            </label>

            {/* Fotos */}
            <label>
              <span style={{ fontSize: 14 }}>
                Fotos (URLs, uma por linha)
              </span>
              <textarea
                value={fotosTexto}
                onChange={(e) => setFotosTexto(e.target.value)}
                rows={3}
                style={{
                  width: "100%",
                  padding: "9px 11px",
                  marginTop: 4,
                  borderRadius: 8,
                  border: "1px solid #334155",
                  
                  resize: "vertical",
                }}
              />
            </label>

            {/* Observações */}
            <label>
              <span style={{ fontSize: 14 }}>Observações</span>
              <textarea
                value={observacoes}
                onChange={(e) => setObservacoes(e.target.value)}
                rows={3}
                style={{
                  width: "100%",
                  padding: "9px 11px",
                  marginTop: 4,
                  borderRadius: 8,
                  border: "1px solid #334155",
                  
                  resize: "vertical",
                }}
              />
            </label>

            {/* Ações */}
            <div
              style={{
                marginTop: 16,
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
              }}
            >
              <button
                type="button"
                className="pc-btn-secundario"
                onClick={() => navigate("/cadastros/pecas")}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="pc-btn-primario"
                disabled={carregando || carregandoFornecedores || carregandoMaquinas}
              >
                {carregando ? "Salvando..." : "Salvar peça"}
              </button>
            </div>
          </form>
        </div>
      </section>
    </LayoutDesktop>
  );
}

export default NovaPeca;
